import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../auth/AuthContext';
import { PERMISSIONS } from '../../constants/permissions';
import {
    filterSidebarSections,
    resolveSidebarWorkspace,
    type MenuItem,
    type SectionGroup,
    type SubMenuItem,
} from './sidebarAccess';
import SidebarFlyout from './SidebarFlyout';
import {
    LayoutDashboard,
    Users,
    UserCircle,
    CreditCard,
    Wallet,
    Landmark,
    Car,
    PieChart,
    BarChart3,
    Settings,
    ShieldCheck,
    FileText,
    FileSearch,
    Phone,
    LayoutGrid,
    DollarSign,
    AlertTriangle,
    Calendar,
    ClipboardCheck,
    Compass,
    IdCard,
    BookOpen,
    Store,
    Banknote,
    Trophy,
    Lightbulb,
    Gift,
    Star,
    Layers,
    Bell,
    Brain,
    Network,
    Globe,
    Boxes,
    ChevronDown,
    ChevronRight,
    PanelLeftClose,
    PanelLeftOpen,
    Home,
    Container,
    ListOrdered,
    Search,
    Activity,
    Database,
    GitBranch,
} from 'lucide-react';
import './Sidebar.css';

const STORAGE_EXPANDED = 'sidebar:expandedMenus';
const STORAGE_SECTIONS = 'sidebar:expandedSections';

export function buildSections(): SectionGroup[] {
    return [
        {
            key: 'customer-ops',
            label: 'Customer Operations',
            icon: <Users size={16} />,
            workspace: 'institution',
            items: [
                { path: '/customers', label: 'Retail Users', icon: <UserCircle size={18} />, requiredPermissions: [PERMISSIONS.CUSTOMERS_VIEW] },
                { path: '/transactions', label: 'Transactions', icon: <CreditCard size={18} />, requiredPermissions: [PERMISSIONS.TRANSACTIONS_VIEW] },
                { path: '/deposit-products', label: 'Deposits', icon: <Wallet size={18} />, requiredPermissions: [PERMISSIONS.SAVINGS_VIEW] },
                {
                    key: 'openbanking-advanced',
                    label: 'Open Banking',
                    icon: <Globe size={18} />,
                    hasSubmenu: true,
                    requiredPermissions: [PERMISSIONS.ACCOUNTS_VIEW],
                    children: [
                        { path: '/openbanking', label: 'Dashboard', icon: <LayoutDashboard size={16} />, requiredPermissions: [PERMISSIONS.ACCOUNTS_VIEW] },
                        { path: '/openbanking/brands', label: 'Brands (14)', icon: <Landmark size={16} />, requiredPermissions: [PERMISSIONS.ACCOUNTS_VIEW] },
                        { path: '/openbanking/transactions', label: 'Transactions', icon: <CreditCard size={16} />, requiredPermissions: [PERMISSIONS.TRANSACTIONS_VIEW] },
                        { path: '/openbanking/webhooks', label: 'Webhooks', icon: <Network size={16} />, requiredPermissions: [PERMISSIONS.SETTINGS_SECURITY] },
                        { path: '/openbanking/analytics', label: 'Analytics', icon: <BarChart3 size={16} />, requiredPermissions: [PERMISSIONS.REPORTS_VIEW] },
                        { path: '/openbanking/settings', label: 'Settings', icon: <Settings size={16} />, requiredPermissions: [PERMISSIONS.SETTINGS_VIEW] },
                    ],
                },
                {
                    key: 'loans',
                    label: 'Loans',
                    icon: <Car size={18} />,
                    hasSubmenu: true,
                    requiredPermissions: [PERMISSIONS.LOANS_VIEW],
                    children: [
                        { path: '/loans', label: 'Dashboard', icon: <LayoutDashboard size={16} />, requiredPermissions: [PERMISSIONS.LOANS_VIEW] },
                        { path: '/loans/pipeline', label: 'Pipeline', icon: <LayoutGrid size={16} />, requiredPermissions: [PERMISSIONS.LOANS_VIEW] },
                        { path: '/loans/applications', label: 'Applications', icon: <FileText size={16} />, requiredPermissions: [PERMISSIONS.LOANS_VIEW] },
                        { path: '/loans/credit-decisioning', label: 'Credit Decisioning', icon: <ShieldCheck size={16} />, requiredPermissions: [PERMISSIONS.LOANS_APPROVE] },
                        { path: '/loans/disbursement', label: 'Disbursement', icon: <DollarSign size={16} />, requiredPermissions: [PERMISSIONS.LOANS_MANAGE] },
                        { path: '/loans/accounts', label: 'Accounts', icon: <BookOpen size={16} />, requiredPermissions: [PERMISSIONS.ACCOUNTS_VIEW] },
                        { path: '/loans/collections', label: 'Collections', icon: <Phone size={16} />, requiredPermissions: [PERMISSIONS.LOANS_MANAGE] },
                        { path: '/loans/portfolio', label: 'Portfolio Risk', icon: <PieChart size={16} />, requiredPermissions: [PERMISSIONS.LOANS_VIEW] },
                        { path: '/loans/products', label: 'Products', icon: <LayoutGrid size={16} />, requiredPermissions: [PERMISSIONS.LOANS_VIEW] },
                        { path: '/loans/reports', label: 'Reports', icon: <BarChart3 size={16} />, requiredPermissions: [PERMISSIONS.REPORTS_VIEW] },
                        { path: '/loans/settings', label: 'Settings', icon: <Settings size={16} />, requiredPermissions: [PERMISSIONS.SETTINGS_VIEW] },
                    ],
                },
                {
                    key: 'mortgages',
                    label: 'Mortgages',
                    icon: <Home size={18} />,
                    hasSubmenu: true,
                    requiredPermissions: [PERMISSIONS.MORTGAGE_VIEW],
                    children: [
                        { path: '/mortgages/pipeline', label: 'Pipeline', icon: <LayoutDashboard size={16} />, requiredPermissions: [PERMISSIONS.MORTGAGE_VIEW] },
                        { path: '/mortgages/products', label: 'Products', icon: <LayoutGrid size={16} />, requiredPermissions: [PERMISSIONS.MORTGAGE_VIEW] },
                        { path: '/mortgages/affordability', label: 'Affordability', icon: <BarChart3 size={16} />, requiredPermissions: [PERMISSIONS.MORTGAGE_VIEW] },
                        { path: '/mortgages/reports', label: 'Reports', icon: <PieChart size={16} />, requiredPermissions: [PERMISSIONS.MORTGAGE_VIEW] },
                    ],
                },
                {
                    key: 'insurance',
                    label: 'Insurance',
                    icon: <ShieldCheck size={18} />,
                    hasSubmenu: true,
                    requiredPermissions: [PERMISSIONS.INSURANCE_POLICIES_VIEW],
                    children: [
                        { path: '/insurance/dashboard', label: 'Dashboard', icon: <LayoutDashboard size={16} />, requiredPermissions: [PERMISSIONS.INSURANCE_POLICIES_VIEW] },
                        { path: '/admin/insurance/products', label: 'Products', icon: <LayoutGrid size={16} />, requiredPermissions: [PERMISSIONS.INSURANCE_PRODUCTS_MANAGE] },
                        { path: '/admin/insurance/quotes', label: 'Quotes', icon: <FileText size={16} />, requiredPermissions: [PERMISSIONS.INSURANCE_POLICIES_MANAGE] },
                        { path: '/admin/insurance/underwriting', label: 'Underwriting', icon: <ClipboardCheck size={16} />, requiredPermissions: [PERMISSIONS.INSURANCE_POLICIES_MANAGE] },
                        { path: '/admin/insurance/claims', label: 'Claims', icon: <AlertTriangle size={16} />, requiredPermissions: [PERMISSIONS.INSURANCE_CLAIMS_MANAGE] },
                    ],
                },
                {
                    key: 'payment-ops',
                    label: 'Payment Ops',
                    icon: <Landmark size={18} />,
                    hasSubmenu: true,
                    requiredPermissions: [PERMISSIONS.PAYMENTS_VIEW],
                    children: [
                        { path: '/payment-ops', label: 'Dashboard', icon: <LayoutDashboard size={16} />, requiredPermissions: [PERMISSIONS.PAYMENTS_VIEW] },
                        { path: '/payment-ops/search', label: 'Payment Search', icon: <Search size={16} />, requiredPermissions: [PERMISSIONS.PAYMENTS_VIEW] },
                        { path: '/payment-ops/reconciliation', label: 'Reconciliation', icon: <BarChart3 size={16} />, requiredPermissions: [PERMISSIONS.PAYMENTS_VIEW] },
                        { path: '/payment-ops/standing-orders', label: 'Standing Orders', icon: <FileText size={16} />, requiredPermissions: [PERMISSIONS.PAYMENTS_VIEW] },
                        { path: '/payment-ops/failed', label: 'Failed Payments', icon: <Banknote size={16} />, requiredPermissions: [PERMISSIONS.PAYMENTS_MANAGE] },
                        { path: '/payment-ops/scheme-routing', label: 'Scheme Routing', icon: <Network size={16} />, requiredPermissions: [PERMISSIONS.PAYMENTS_SCHEME_CONFIG] },
                    ],
                },
                {
                    key: 'general-ledger',
                    label: 'General Ledger',
                    icon: <BookOpen size={18} />,
                    hasSubmenu: true,
                    requiredPermissions: [PERMISSIONS.GL_VIEW],
                    children: [
                        { path: '/general-ledger', label: 'Dashboard', icon: <LayoutDashboard size={16} />, requiredPermissions: [PERMISSIONS.GL_VIEW] },
                        { path: '/general-ledger/accounts', label: 'Chart of Accounts', icon: <BookOpen size={16} />, requiredPermissions: [PERMISSIONS.GL_VIEW] },
                        { path: '/general-ledger/journals', label: 'Journal Entries', icon: <FileText size={16} />, requiredPermissions: [PERMISSIONS.GL_VIEW] },
                        { path: '/general-ledger/trial-balance', label: 'Trial Balance', icon: <BarChart3 size={16} />, requiredPermissions: [PERMISSIONS.GL_VIEW] },
                    ],
                },
            ],
        },
        {
            key: 'branch-banking',
            label: 'Branch Banking',
            icon: <Store size={16} />,
            workspace: 'institution',
            items: [
                {
                    key: 'branch',
                    label: 'Branch Banking',
                    icon: <Store size={18} />,
                    hasSubmenu: true,
                    requiredPermissions: [PERMISSIONS.BRANCH_BANKING_ACCESS],
                    children: [
                        { path: '/branch/dashboard', label: 'Dashboard', icon: <LayoutDashboard size={16} />, requiredPermissions: [PERMISSIONS.BRANCH_BANKING_ACCESS] },
                        { path: '/branch/customers', label: 'Customer Lookup', icon: <UserCircle size={16} />, requiredPermissions: [PERMISSIONS.BRANCH_BANKING_ACCESS] },
                        { path: '/branch/accounts/open', label: 'Account Opening', icon: <Container size={16} />, requiredPermissions: [PERMISSIONS.BRANCH_ACCOUNTS_OPEN] },
                        { path: '/branch/transactions', label: 'Transactions', icon: <Banknote size={16} />, requiredPermissions: [PERMISSIONS.BRANCH_TRANSACTIONS_CREATE] },
                        { path: '/branch/cash', label: 'Cash Management', icon: <DollarSign size={16} />, requiredPermissions: [PERMISSIONS.BRANCH_CASH_MANAGE] },
                        { path: '/branch/queue', label: 'Queue', icon: <ListOrdered size={16} />, requiredPermissions: [PERMISSIONS.BRANCH_BANKING_ACCESS] },
                    ],
                },
            ],
        },
        {
            key: 'compliance-risk',
            label: 'Compliance & Risk',
            icon: <ShieldCheck size={16} />,
            workspace: 'institution',
            items: [
                {
                    key: 'kyc',
                    label: 'KYC Management',
                    icon: <ClipboardCheck size={18} />,
                    hasSubmenu: true,
                    requiredPermissions: [PERMISSIONS.COMPLIANCE_VIEW],
                    children: [
                        { path: '/kyc', label: 'Dashboard', icon: <LayoutDashboard size={16} />, requiredPermissions: [PERMISSIONS.COMPLIANCE_VIEW] },
                        { path: '/kyc/reviews', label: 'Reviews', icon: <ClipboardCheck size={16} />, requiredPermissions: [PERMISSIONS.COMPLIANCE_MANAGE] },
                        { path: '/kyc/analytics', label: 'Analytics', icon: <BarChart3 size={16} />, requiredPermissions: [PERMISSIONS.COMPLIANCE_VIEW] },
                    ],
                },
                {
                    key: 'compliance',
                    label: 'Compliance',
                    icon: <ShieldCheck size={18} />,
                    hasSubmenu: true,
                    requiredPermissions: [PERMISSIONS.COMPLIANCE_VIEW],
                    children: [
                        { path: '/compliance', label: 'Dashboard', icon: <LayoutDashboard size={16} />, requiredPermissions: [PERMISSIONS.COMPLIANCE_VIEW] },
                        { path: '/compliance/alerts', label: 'Alert Management', icon: <AlertTriangle size={16} />, requiredPermissions: [PERMISSIONS.COMPLIANCE_VIEW] },
                        { path: '/compliance/sar', label: 'SAR Filing', icon: <FileText size={16} />, requiredPermissions: [PERMISSIONS.COMPLIANCE_SAR_FILE] },
                        { path: '/compliance/regulatory-returns', label: 'Regulatory Returns', icon: <Calendar size={16} />, requiredPermissions: [PERMISSIONS.COMPLIANCE_VIEW] },
                        { path: '/compliance/sanctions', label: 'Sanctions Screening', icon: <ClipboardCheck size={16} />, requiredPermissions: [PERMISSIONS.COMPLIANCE_SANCTIONS_SCREEN] },
                    ],
                },
                {
                    key: 'audit',
                    label: 'Audit Trail',
                    icon: <FileSearch size={18} />,
                    hasSubmenu: true,
                    requiredPermissions: [PERMISSIONS.AUDIT_VIEW],
                    children: [
                        { path: '/audit', label: 'Dashboard', icon: <LayoutDashboard size={16} />, requiredPermissions: [PERMISSIONS.AUDIT_VIEW] },
                        { path: '/audit/log', label: 'Event Log', icon: <FileText size={16} />, requiredPermissions: [PERMISSIONS.AUDIT_VIEW] },
                        { path: '/audit/users', label: 'User Activity', icon: <Users size={16} />, requiredPermissions: [PERMISSIONS.AUDIT_VIEW] },
                    ],
                },
            ],
        },
        {
            key: 'products-config',
            label: 'Products & Config',
            icon: <Boxes size={16} />,
            workspace: 'institution',
            items: [
                { path: '/smart-panel', label: 'Smart Panel', icon: <Bell size={18} />, requiredPermissions: [PERMISSIONS.SETTINGS_EDIT] },
            ],
        },
        {
            key: 'ai-intelligence',
            label: 'AI & Intelligence',
            icon: <Brain size={16} />,
            workspace: 'institution',
            items: [
                { path: '/insights', label: 'Insights', icon: <Lightbulb size={18} />, requiredPermissions: [PERMISSIONS.INSTITUTION_INSIGHTS_VIEW] },
                { path: '/profile-traits', label: 'Profile Traits', icon: <IdCard size={18} />, requiredPermissions: [PERMISSIONS.INSTITUTION_PROFILE_TRAITS_VIEW] },
                { path: '/admin/moni-ai/feedback', label: 'Moni AI Feedback', icon: <AlertTriangle size={18} />, requiredPermissions: [PERMISSIONS.INSTITUTION_MONI_FEEDBACK_VIEW] },
                {
                    key: 'rewards',
                    label: 'Rewards',
                    icon: <Trophy size={18} />,
                    hasSubmenu: true,
                    requiredPermissions: [PERMISSIONS.INSTITUTION_REWARDS_VIEW],
                    children: [
                        { path: '/rewards/config', label: 'Configuration', icon: <Settings size={16} />, requiredPermissions: [PERMISSIONS.INSTITUTION_REWARDS_VIEW] },
                        { path: '/rewards/badges', label: 'Badges', icon: <Star size={16} />, requiredPermissions: [PERMISSIONS.INSTITUTION_REWARDS_VIEW] },
                        { path: '/rewards/tiers', label: 'Tiers', icon: <Layers size={16} />, requiredPermissions: [PERMISSIONS.INSTITUTION_REWARDS_VIEW] },
                        { path: '/rewards/offers', label: 'Offers', icon: <Gift size={16} />, requiredPermissions: [PERMISSIONS.INSTITUTION_REWARDS_VIEW] },
                        { path: '/rewards/analytics', label: 'Analytics', icon: <PieChart size={16} />, requiredPermissions: [PERMISSIONS.INSTITUTION_REWARDS_VIEW] },
                    ],
                },
                { path: '/journey-templates', label: 'Journeys', icon: <Compass size={18} />, requiredPermissions: [PERMISSIONS.INSTITUTION_JOURNEYS_VIEW] },
            ],
        },
        {
            key: 'platform',
            label: 'Platform',
            icon: <Globe size={16} />,
            workspace: 'platform',
            items: [
                { path: '/api-management', label: 'API Management', icon: <Network size={18} />, requiredPermissions: [PERMISSIONS.SETTINGS_SECURITY] },
                { path: '/gateway/routes', label: 'Gateway Routes', icon: <GitBranch size={18} />, requiredPermissions: [PERMISSIONS.PLATFORM_ROUTES_MANAGE] },
                { path: '/platform/health', label: 'System Health', icon: <Activity size={18} />, requiredPermissions: [PERMISSIONS.PLATFORM_HEALTH_VIEW] },
                { path: '/platform/metering', label: 'Metering & Billing', icon: <BarChart3 size={18} />, requiredPermissions: [PERMISSIONS.PLATFORM_METERING_VIEW] },
                { path: '/platform/migration', label: 'Migration Console', icon: <Database size={18} />, requiredPermissions: [PERMISSIONS.CONFIG_MANAGE] },
            ],
        },
        {
            key: 'administration',
            label: 'Administration',
            icon: <Settings size={16} />,
            workspace: 'shared',
            items: [
                { path: '/users', label: 'Users', icon: <Users size={18} />, requiredPermissions: [PERMISSIONS.USERS_VIEW] },
                { path: '/institutions', label: 'Institutions', icon: <Landmark size={18} />, requiredPermissions: [PERMISSIONS.PLATFORM_INSTITUTIONS_MANAGE], workspace: 'platform' },
                { path: '/settings', label: 'Settings', icon: <Settings size={18} />, requiredPermissions: [PERMISSIONS.SETTINGS_VIEW] },
            ],
        },
    ];
}

interface FlyoutState {
    item: MenuItem;
    anchorElement: HTMLButtonElement;
}

const Sidebar: React.FC = () => {
    const [isCollapsed, setIsCollapsed] = useState(true);
    const [expandedMenus, setExpandedMenus] = useState<Record<string, boolean>>(() => {
        const saved = localStorage.getItem(STORAGE_EXPANDED);
        return saved ? JSON.parse(saved) : {};
    });
    const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>(() => {
        const saved = localStorage.getItem(STORAGE_SECTIONS);
        return saved ? JSON.parse(saved) : {};
    });
    const [flyout, setFlyout] = useState<FlyoutState | null>(null);

    const location = useLocation();
    const navigate = useNavigate();
    const {
        effectiveRole,
        hasAnyPermission,
        originalUser,
        permissionsLoading,
        user,
    } = useAuth();
    const originalRole = originalUser?.role ?? user?.originalRole ?? user?.role;
    const workspace = resolveSidebarWorkspace(effectiveRole);

    // Persist state
    useEffect(() => { localStorage.setItem(STORAGE_EXPANDED, JSON.stringify(expandedMenus)); }, [expandedMenus]);
    useEffect(() => { localStorage.setItem(STORAGE_SECTIONS, JSON.stringify(expandedSections)); }, [expandedSections]);
    useEffect(() => {
        document.documentElement.dataset.sidebarState = isCollapsed ? 'collapsed' : 'expanded';
        return () => {
            delete document.documentElement.dataset.sidebarState;
        };
    }, [isCollapsed]);

    // Auto-expand section + submenu when navigating to a deep route
    useEffect(() => {
        const sections = buildSections();
        for (const section of sections) {
            for (const item of section.items) {
                if (item.hasSubmenu && item.key && item.children) {
                    const match = item.children.some(c => location.pathname.startsWith(c.path));
                    if (match) {
                        setExpandedSections(prev => {
                            if (prev[section.key]) return prev;
                            return { ...prev, [section.key]: true };
                        });
                        setExpandedMenus(prev => {
                            if (prev[item.key!]) return prev;
                            return { ...prev, [item.key!]: true };
                        });
                    }
                }
            }
        }
    }, [location.pathname]);

    // Responsive: auto-collapse on tablet
    useEffect(() => {
        const mq = window.matchMedia('(max-width: 1024px)');
        const handler = (e: MediaQueryListEvent) => { if (e.matches) setIsCollapsed(true); };
        mq.addEventListener('change', handler);
        if (mq.matches) setIsCollapsed(true);
        return () => mq.removeEventListener('change', handler);
    }, []);

    const closeFlyout = useCallback(() => setFlyout(null), []);
    useEffect(() => {
        closeFlyout();
    }, [closeFlyout, location.pathname]);

    useEffect(() => {
        if (!isCollapsed) closeFlyout();
    }, [closeFlyout, isCollapsed]);

    const handleToggle = useCallback(() => {
        closeFlyout();
        setIsCollapsed(prev => !prev);
    }, [closeFlyout]);

    const toggleSubmenu = useCallback((menuKey: string) => {
        setExpandedMenus(prev => ({ ...prev, [menuKey]: !prev[menuKey] }));
    }, []);

    const toggleSection = useCallback((sectionKey: string) => {
        setExpandedSections(prev => ({ ...prev, [sectionKey]: !prev[sectionKey] }));
    }, []);

    // Build filtered sections
    const sections = useMemo(
        () => permissionsLoading
            ? []
            : filterSidebarSections(buildSections(), {
                workspace,
                originalRole,
                hasAnyPermission,
            }),
        [hasAnyPermission, originalRole, permissionsLoading, workspace],
    );

    // Active-state helpers
    const isItemActive = (item: MenuItem): boolean => {
        if (item.hasSubmenu && item.children) {
            return item.children.some(c => location.pathname === c.path || location.pathname.startsWith(c.path + '/'));
        }
        if (item.path === '/insurance/dashboard') return location.pathname.startsWith('/insurance');
        if (item.path === '/gateway/routes') return location.pathname.startsWith('/gateway/routes');
        return location.pathname === item.path;
    };

    const isSubmenuActive = (child: SubMenuItem): boolean => location.pathname === child.path;
    const isMenuExpanded = (item: MenuItem): boolean => Boolean(
        item.hasSubmenu &&
        item.key &&
        (isCollapsed
            ? flyout?.item.key === item.key
            : expandedMenus[item.key]),
    );

    const sidebarToggleLabel = isCollapsed ? 'Keep sidebar open' : 'Collapse sidebar';

    return (
        <>
            <aside className={`sidebar ${isCollapsed ? 'sidebar--collapsed' : 'sidebar--expanded'}`} role="navigation" aria-label="Main navigation">
            {/* Header */}
            <div className="sidebar__header">
                <button
                    className="sidebar__toggle"
                    onClick={handleToggle}
                    title={sidebarToggleLabel}
                    aria-label={sidebarToggleLabel}
                    aria-pressed={!isCollapsed}
                    type="button"
                >
                    {isCollapsed ? <PanelLeftOpen size={16} /> : <PanelLeftClose size={16} />}
                </button>
            </div>

            <nav className="sidebar__nav">
                {/* Dashboard — always visible */}
                <div className="sidebar__section">
                    <button
                        className={`sidebar__menu-item ${location.pathname === '/dashboard' || location.pathname === '/' ? 'sidebar__menu-item--active' : ''}`}
                        role="menuitem"
                        aria-current={location.pathname === '/dashboard' ? 'page' : undefined}
                        aria-label="Dashboard"
                        onClick={() => navigate('/dashboard')}
                        title={isCollapsed ? 'Dashboard' : ''}
                    >
                        <span className="sidebar__menu-icon"><LayoutDashboard size={18} /></span>
                        {!isCollapsed && <span className="sidebar__menu-label">Dashboard</span>}
                    </button>
                </div>

                {/* Section groups */}
                {sections.map(section => {
                    const isSectionOpen = expandedSections[section.key] !== false; // default open
                    return (
                        <div key={section.key} className="sidebar__section">
                            {!isCollapsed && (
                                <button
                                    className="sidebar__section-header"
                                    onClick={() => toggleSection(section.key)}
                                    aria-expanded={isSectionOpen}
                                >
                                    <span className="sidebar__section-icon">{section.icon}</span>
                                    <span className="sidebar__section-label">{section.label}</span>
                                    {isSectionOpen
                                        ? <ChevronDown size={14} className="sidebar__section-chevron" />
                                        : <ChevronRight size={14} className="sidebar__section-chevron" />}
                                </button>
                            )}

                            {(isCollapsed || isSectionOpen) && section.items.map(item => (
                                <div key={item.key || item.path}>
                                    <button
                                        className={`sidebar__menu-item ${isItemActive(item) ? 'sidebar__menu-item--active' : ''}`}
                                        role="menuitem"
                                        aria-current={isItemActive(item) && !item.hasSubmenu ? 'page' : undefined}
                                        aria-expanded={item.hasSubmenu ? isMenuExpanded(item) : undefined}
                                        aria-haspopup={item.hasSubmenu ? 'menu' : undefined}
                                        aria-label={item.hasSubmenu ? `${item.label} menu` : item.label}
                                        onClick={(event) => {
                                            if (item.hasSubmenu && item.key) {
                                                if (isCollapsed) {
                                                    setFlyout(current => current?.item.key === item.key
                                                        ? null
                                                        : {
                                                            item,
                                                            anchorElement: event.currentTarget,
                                                        });
                                                } else {
                                                    toggleSubmenu(item.key);
                                                }
                                            } else if (item.path) {
                                                closeFlyout();
                                                navigate(item.path);
                                            }
                                        }}
                                        title={isCollapsed
                                            ? item.hasSubmenu
                                                ? `${item.label} menu`
                                                : item.label
                                            : ''}
                                    >
                                        <span className="sidebar__menu-icon">{item.icon}</span>
                                        {isCollapsed && item.hasSubmenu && (
                                            <span className="sidebar__group-indicator" aria-hidden="true">
                                                <ChevronRight />
                                            </span>
                                        )}
                                        {!isCollapsed && (
                                            <>
                                                <span className="sidebar__menu-label">{item.label}</span>
                                                {item.hasSubmenu && (
                                                    <span
                                                        className={`sidebar__submenu-toggle ${item.key && expandedMenus[item.key] ? 'sidebar__submenu-toggle--expanded' : ''}`}
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            e.stopPropagation();
                                                            if (item.key) toggleSubmenu(item.key);
                                                        }}
                                                    >
                                                        <ChevronRight size={14} />
                                                    </span>
                                                )}
                                            </>
                                        )}
                                    </button>

                                    {/* Submenu children */}
                                    {item.hasSubmenu && !isCollapsed && item.key && expandedMenus[item.key] && item.children && (
                                        <div className="sidebar__submenu">
                                            {item.children.map(child => (
                                                <button
                                                    key={child.path}
                                                    className={`sidebar__submenu-item ${isSubmenuActive(child) ? 'sidebar__submenu-item--active' : ''}`}
                                                    role="menuitem"
                                                    aria-current={isSubmenuActive(child) ? 'page' : undefined}
                                                    onClick={() => navigate(child.path)}
                                                >
                                                    <span className="sidebar__submenu-icon">{child.icon}</span>
                                                    <span className="sidebar__menu-label">{child.label}</span>
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    );
                })}
            </nav>

            {!isCollapsed && (
                <div className="sidebar__footer">
                    <div className="sidebar__version">Version 2.0.0</div>
                </div>
            )}
            </aside>
            {isCollapsed && flyout && (
                <SidebarFlyout
                    item={flyout.item}
                    anchorElement={flyout.anchorElement}
                    activePath={location.pathname}
                    onDismiss={closeFlyout}
                    onNavigate={(path) => {
                        closeFlyout();
                        navigate(path);
                    }}
                />
            )}
        </>
    );
};

export default Sidebar;
